import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Network, Plus, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader, Section, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Card, CardHeader, CardTitle, CardBody, Badge, Skeleton, IconButton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useVpc, useDeleteSubnet, type VpcSubnet } from '@/api/opennebula';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { CopyIp, CreateSubnetDialog, EditSubnetDialog } from './VpcDialogs';

export function VpcDetail() {
  const { serviceId, vpcId } = useParams<{ serviceId: string; vpcId: string }>();
  const navigate = useNavigate();
  const vpcsPath = `/services/compute/${serviceId}/vpc`;
  const { data: vpc, isLoading } = useVpc(serviceId, vpcId);

  const crumbs = (current: string) => (
    <Breadcrumbs>
      <BreadcrumbItem href={vpcsPath} onClick={(e) => { e.preventDefault(); navigate(vpcsPath); }}>VPC</BreadcrumbItem>
      <BreadcrumbItem current>{current}</BreadcrumbItem>
    </Breadcrumbs>
  );

  if (isLoading || !vpc) {
    return (
      <Container size="xl">
        <PageHeader breadcrumbs={crumbs('VPC')} icon={<Network size={22} />} title={<Skeleton style={{ height: 24, width: 200 }} />} />
        <Skeleton style={{ height: 160 }} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={crumbs(vpc.name || vpc.id)}
        icon={<Network size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            {vpc.name || vpc.id}
            {vpc.status && <StatusPill tone={/active|ready|running/i.test(vpc.status) ? 'success' : 'muted'}>{vpc.status}</StatusPill>}
          </span>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card flat>
          <CardHeader><CardTitle>VPC information</CardTitle></CardHeader>
          <CardBody>
            <DescList>
              <DescItem label="Name">{vpc.name || '—'}</DescItem>
              <DescItem label="CIDR block"><code style={monoValue}>{vpc.cidr || '—'}</code></DescItem>
              <DescItem label="Shared">{vpc.is_shared ? 'Yes' : 'No'}</DescItem>
              {vpc.description && <DescItem label="Description">{vpc.description}</DescItem>}
              <DescItem label="VPC ID"><code style={monoValue}>{vpc.id}</code></DescItem>
            </DescList>
          </CardBody>
        </Card>

        {vpc.router && (
          <Card flat>
            <CardHeader><CardTitle>Router</CardTitle></CardHeader>
            <CardBody>
              <DescList>
                <DescItem label="Name">
                  <a
                    href={`${vpcsPath.replace(/\/vpc$/, '')}/networks/routers/${vpc.router.id}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/services/compute/${serviceId}/networks/routers/${vpc.router!.id}`); }}
                    style={{ color: 'var(--vn-primary-text)' }}
                  >
                    {vpc.router.name || vpc.router.id}
                  </a>
                </DescItem>
                <DescItem label="Status">{vpc.router.status ? <StatusPill tone={/active|ready|running/i.test(vpc.router.status) ? 'success' : 'muted'}>{vpc.router.status}</StatusPill> : '—'}</DescItem>
                <DescItem label="Public IP">{vpc.router.public_ip ? <CopyIp ip={vpc.router.public_ip} /> : '—'}</DescItem>
                {vpc.router.description && <DescItem label="Description">{vpc.router.description}</DescItem>}
              </DescList>
            </CardBody>
          </Card>
        )}

        <Subnets serviceId={serviceId!} vpcId={vpcId!} subnets={vpc.subnets ?? []} />
      </div>
    </Container>
  );
}

function Subnets({ serviceId, vpcId, subnets }: { serviceId: string; vpcId: string; subnets: VpcSubnet[] }) {
  const { show } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editSub, setEditSub] = useState<VpcSubnet | null>(null);
  const [delSub, setDelSub] = useState<VpcSubnet | null>(null);

  const del = useDeleteSubnet(serviceId, vpcId, { onSuccess: () => { show({ tone: 'success', title: 'Subnet deleted' }); setDelSub(null); }, onError: () => show({ tone: 'danger', title: 'Delete failed' }) });

  const columns: ColumnDef<VpcSubnet, unknown>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</span> },
    { id: 'cidr', header: 'CIDR', cell: ({ row }) => <code style={monoValue}>{row.original.cidr || '—'}</code> },
    { id: 'gateway', header: 'Gateway', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.gateway || '—'}</span> },
    { id: 'leased', header: 'Leased', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.leased ?? 0}{row.original.size ? `/${row.original.size}` : ''}</span> },
    { id: 'shared', header: 'Shared', cell: ({ row }) => row.original.is_shared ? <Badge tone="neutral" appearance="subtle" size="sm">Shared</Badge> : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span> },
    { id: 'actions', header: '', cell: ({ row }) => (
      <span style={{ display: 'flex', gap: 'var(--vn-space-1)', justifyContent: 'flex-end' }}>
        <IconButton variant="ghost" size="sm" aria-label="Edit subnet" onClick={() => setEditSub(row.original)}><Pencil size={15} /></IconButton>
        <IconButton variant="ghost" size="sm" aria-label="Delete subnet" onClick={() => setDelSub(row.original)}><Trash2 size={15} /></IconButton>
      </span>
    ) },
  ];

  return (
    <Section
      title="Subnets"
      description="Subnets carve the VPC CIDR into routable address ranges."
      actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Add subnet</Button>}
      style={{ marginBottom: 0 }}
    >
      {/* DataTable carries its own bordered frame — don't wrap it in a Card (double border). */}
      <DataTable
        columns={columns}
        data={subnets}
        getRowId={(s) => String(s.id)}
        emptyState={<EmptyState icon={<Network size={24} />} title="No subnets" description="Add a subnet within this VPC's CIDR." actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>Add subnet</Button>} />}
      />

      <CreateSubnetDialog open={createOpen} onOpenChange={setCreateOpen} serviceId={serviceId} vpcId={vpcId} />
      {editSub && <EditSubnetDialog serviceId={serviceId} vpcId={vpcId} subnet={editSub} onClose={() => setEditSub(null)} />}
      <ConfirmDialog open={!!delSub} onOpenChange={(o) => !o && setDelSub(null)} title="Delete subnet?" description={`Deleting “${delSub?.name || delSub?.id}” also removes the underlying network. Ensure no instances are attached.`} confirmLabel="Delete" destructive loading={del.isPending} onConfirm={() => delSub && del.mutate(delSub.id)} />
    </Section>
  );
}
